import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowUp, ChevronLeft, LayoutDashboard, X, XCircle } from 'lucide-react'
import { backLabel as backName, useBack } from '@/hooks/useBack'
import { RESTORE_SCROLL } from '@/lib/routeScrollMemory'
import { useQuizStore, readLastSession, syncPendingSessionToCloud } from '@/stores/quizStore'
import type { CompletedSession } from '@/stores/quizStore'
import { useAuth } from '@/hooks/useAuth'
import { useConceptMastery } from '@/hooks/useConceptMastery'
import { loadCachedStudyPlan, todayISO } from '@/lib/studyPlan'
import { QuestionCard } from '@/components/QuestionCard'
import { ConceptCoverageSection } from '@/components/ConceptCoverageSection'
import { effectiveOutcome, formatScore, isUnanswered, quizPoints, scoreColorClass, scoredQuestions, skipsUnanswered } from '@/lib/quizScore'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Loader2 } from 'lucide-react'
import type { MasteryState } from '@/lib/mastery'
import { buildMasteryLookup, resolveConceptState } from '@/lib/conceptMatch'
import { ConceptPopup } from '@/components/wiki/ConceptPopup'
import { QuestCompleteOverlay } from '@/components/QuestCompleteOverlay'
import { StreakCompleteOverlay } from '@/components/StreakCompleteOverlay'
import { StudyPlanCompleteOverlay } from '@/components/StudyPlanCompleteOverlay'
import { ConceptLevelUpCeremony } from '@/components/ConceptLevelUpCeremony'
import { QUESTS_ENABLED, STREAK_ENABLED } from '@/lib/featureFlags'
import { readJustCompletedQuests } from '@/lib/questStore'
import { EXAM_LABEL_TO_ID } from '@/lib/examIds'
import { getDailyGems } from '@/lib/dailyProgressStore'
import { useGems } from '@/hooks/useGems'

// Shared "quiz these exact questions" seam (same key RecentMistakesCard and
// MasteryAnalyticsCard write) — keeps the retry URL small for a long miss list.
const SELECTED_IDS_KEY = 'actuarial_selected_ids'

// Mirrors the target-state progression used by TodayCard/ReadinessCard to
// decide whether a concept has reached today's assigned goal.
const NEXT_STATE: Partial<Record<MasteryState, MasteryState>> = {
  new: 'level1', forgotten: 'level1', level1: 'level2', level2: 'level3',
}
const STATE_ORDER: Record<MasteryState, number> = {
  new: 0, forgotten: 0, level1: 1, level2: 2, level3: 3,
}

// ─── Post-quiz celebrations ───────────────────────────────────────────────────

// Sequences the two post-quiz celebrations so the streak flame plays first and
// the quest collect prompt follows. The streak overlay resolves as soon as it's
// dismissed — or immediately when the streak didn't grow — flipping to the quest
// overlay. When a streak can't run (feature off, or no correct answer today), we
// start already resolved so quests appear right away.
function PostQuizCelebrations({ streakEligible }: { streakEligible: boolean }) {
  const [streakDone, setStreakDone] = useState(!streakEligible)
  return (
    <>
      {streakEligible && !streakDone && (
        <StreakCompleteOverlay onResolved={() => setStreakDone(true)} />
      )}
      {QUESTS_ENABLED && streakDone && <QuestCompleteOverlay />}
    </>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Review() {
  const navigate = useNavigate()
  const back = useBack({ to: '/', label: 'Quiz' }, { skip: path => path.split(/[?#]/)[0] === '/quiz' })
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const { records: masteryRecords, loading: masteryLoading } = useConceptMastery()
  const { balance: gemBalance, loading: gemsLoading } = useGems()
  const { resetQuiz } = useQuizStore()
  const [session, setSession] = useState<CompletedSession | null>(null)
  // Gates the streak/quest/plan celebrations until the level-up ceremony (if any)
  // has been dismissed, so they play one after another rather than stacking.
  const [levelUpsDone, setLevelUpsDone] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(null)
  const [showIncorrectOnly, setShowIncorrectOnly] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [actionsPinned, setActionsPinned] = useState(false)
  const questionReviewRef = useRef<HTMLDivElement>(null)
  const actionsRowRef = useRef<HTMLDivElement>(null)
  const pinnedHeaderRef = useRef<HTMLDivElement>(null)
  const syncingRef = useRef(false)

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Reveal the pinned header once the full-size actions row (Go to Dashboard /
  // Review Incorrect) has scrolled up behind the sticky line — same measurement
  // the Dashboard uses for its compact actions. The threshold comes from the
  // pinned header's own wrapper, which is zero-height and always mounted, so it
  // reads the real sticky offset at every breakpoint and the bar fading in
  // can't move the threshold and make the state flicker.
  useEffect(() => {
    let raf = 0
    const measure = () => {
      raf = 0
      const actions = actionsRowRef.current
      const anchor = pinnedHeaderRef.current
      if (!actions || !anchor) {
        setActionsPinned(false)
        return
      }
      setActionsPinned(actions.getBoundingClientRect().bottom < anchor.getBoundingClientRect().top)
    }
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure) }
    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [session])

  useEffect(() => {
    const last = readLastSession()
    if (last) {
      setSession(last)
    } else {
      console.warn('Review: no completed session in localStorage; redirecting to /')
      navigate('/', { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If this quiz was completed while signed out (session.needsCloudSync), and
  // the user has since signed in or created an account from this screen's
  // "Sign In" prompt, persist it to their account now — same DB writes as if
  // they'd been logged in when they finished the quiz.
  useEffect(() => {
    if (!user || !session?.needsCloudSync || masteryLoading || syncingRef.current) return
    syncingRef.current = true
    syncPendingSessionToCloud(user.id, masteryRecords)
      .then(synced => {
        if (synced) setSession(readLastSession())
      })
      .catch(err => console.error('Failed to sync pending quiz session:', err))
      .finally(() => { syncingRef.current = false })
  }, [user, session, masteryLoading, masteryRecords])

  const progressKey = useMemo(() => {
    if (!session) return null
    return EXAM_LABEL_TO_ID[session.questions[0]?.exam ?? ''] ?? null
  }, [session])

  const studyPlan = useMemo(() => {
    return progressKey ? loadCachedStudyPlan(progressKey) : null
  }, [progressKey])

  const newlyCompletedSlugs = useMemo(() => {
    const slugs = new Set<string>()
    for (const t of session?.masteryTransitions ?? []) {
      if (t.to === 'level1' || t.to === 'level2' || t.to === 'level3') {
        slugs.add(t.conceptSlug.toLowerCase())
      }
    }
    return slugs
  }, [session])

  // Today's Study Plan completion — mirrors ReadinessCard's "all concepts on
  // target" check, but scoped to what's needed here so we can surface the 2×
  // bonus unlock right after the quiz that finished it, instead of waiting for
  // the user to return to the Dashboard.
  const planJustCompleted = useMemo(() => {
    if (!progressKey || !studyPlan || masteryLoading) return false
    if (studyPlan.status === 'review_mode') return false
    const concepts = studyPlan.todaysConcepts
    if (concepts.length === 0) return false
    const lookup = buildMasteryLookup(masteryRecords.filter(r => r.exam_id === progressKey))
    const now = new Date()
    return concepts.every(name => {
      const current = resolveConceptState(lookup, { name }, now)
      const target: MasteryState = current === 'level3' ? 'level3' : (NEXT_STATE[current] ?? 'level1')
      const advancedToday = newlyCompletedSlugs.has(name.toLowerCase())
      return STATE_ORDER[current] >= STATE_ORDER[target] || advancedToday
    })
  }, [progressKey, studyPlan, masteryLoading, masteryRecords, newlyCompletedSlugs])

  const [showPlanBonus, setShowPlanBonus] = useState(false)
  const planBonusHandledRef = useRef(false)

  useEffect(() => {
    if (planBonusHandledRef.current || !planJustCompleted || !progressKey) return
    planBonusHandledRef.current = true
    // Let a quest celebration take priority if both landed on this quiz —
    // the Dashboard's own bonus indicator still claims it silently later.
    if (QUESTS_ENABLED && readJustCompletedQuests().length > 0) return
    try {
      const raw = localStorage.getItem(`actuarial_daily_bonus_${progressKey}_${todayISO()}`)
      const alreadyClaimed = raw ? !!(JSON.parse(raw) as { amount?: number }).amount : false
      if (!alreadyClaimed) setShowPlanBonus(true)
    } catch { /* ignore */ }
  }, [planJustCompleted, progressKey])

  // When user clicks a radial segment, select it and scroll to the question review
  function handleQuestionSelect(idx: number | null) {
    setSelectedQuestion(idx)
    if (idx !== null) {
      setShowIncorrectOnly(false)
      // Small delay so React can render before scrolling
      setTimeout(() => {
        questionReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 80)
    }
  }

  function handleReviewIncorrect() {
    setSelectedQuestion(null)
    setShowIncorrectOnly(true)
    setTimeout(() => {
      questionReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const { correctCount, totalQuestions, timeTakenSeconds } = session
  // Score by points with partial credit: each question earns its fraction of
  // what it is worth (a 2.5-point question counts five times a 0.5-point one),
  // rather than counting as a full hit or miss. correctCount stays the
  // whole-question count that drives gems/streak; points is what the results
  // screen displays.
  // A quiz finished early is scored on what was answered; its untried questions
  // are Unanswered, not wrong. A practice exam scores every question.
  const points = quizPoints(
    scoredQuestions(session.questions, session.responses, session.mode, session.manualGrades ?? {}),
    session.responses,
    session.manualGrades ?? {},
  )
  const { percentage } = points

  // Level-ups the quiz produced. They drive the ceremony — where a New concept
  // reaching Level 1 plays the collect animation — and the results card below.
  const upwardTransitions = session.masteryTransitions?.filter(
    t => t.to === 'level1' || t.to === 'level2' || t.to === 'level3'
  ) ?? []

  // Which questions to show in the review list
  const outcomes = session.questions.map(q =>
    effectiveOutcome(q, session.responses[q.id]?.chosen, session.manualGrades ?? {})
  )
  const unanswered = session.questions.map(q =>
    skipsUnanswered(session.mode) && isUnanswered(q, session.responses, session.manualGrades ?? {})
  )
  const visibleQuestions = selectedQuestion !== null
    ? session.questions.filter((_, i) => i === selectedQuestion)
    : showIncorrectOnly
    ? session.questions.filter((_, i) => !outcomes[i] && !unanswered[i])
    : session.questions

  // What "Try Again" re-quizzes: only the questions this session didn't get full
  // credit on. A partial (e.g. a multi-part with some parts missed) counts as
  // missed, and so does a question left unanswered. Nothing missed → nothing to
  // retry, so the button drops out and only "New Quiz" remains.
  const retryIds = session.questions.filter((_, i) => !outcomes[i]).map(q => q.id)
  const unansweredCount = unanswered.filter(Boolean).length
  const missedCount = retryIds.length - unansweredCount
  const retryMode = session.mode

  // A new quiz is set up in the builder this one came from, when it came from
  // one — back to that exam, its choices as they were — and in the Quiz tab
  // otherwise.
  function handleNewQuiz() {
    resetQuiz()
    if (back.fromTrail && back.to.split(/[?#]/)[0] === '/') back.go()
    else navigate('/')
  }

  function handleTryAgain() {
    if (retryIds.length === 0) return
    try {
      sessionStorage.setItem(SELECTED_IDS_KEY, JSON.stringify(retryIds))
    } catch {
      /* ignore quota/private-mode errors */
    }
    // Build the params from scratch rather than reusing this page's: the original
    // topic/concept/count filters would be dead weight (an ids selection wins in
    // filterQuestions) and a stale `count` could truncate the retry set.
    const params = new URLSearchParams({
      selection: 'stored',
      mode: retryMode,
      count: String(retryIds.length),
    })
    // Carry the reveal the finished session ran under; with none in the URL the
    // quiz falls back to the reader's saved choice for this mode, which is a
    // better guess than pinning the retry to 'during'.
    const reveal = searchParams.get('reveal')
    if (reveal) params.set('reveal', reveal)
    const from = searchParams.get('from')
    if (from) params.set('from', from)
    resetQuiz()
    navigate(`/quiz?${params.toString()}`)
  }

  // Full-screen ceremony for concepts levelled up by this quiz — plays first,
  // then hands off to the streak/quest/plan celebrations. For signed-in users we
  // wait for the gem balance to load so the running tally lands on the right
  // total; guests earn no gems so there's nothing to wait on.
  const hasLevelUps = upwardTransitions.length > 0
  const levelUpCeremonyReady = !user || !gemsLoading
  const showLevelUpCeremony = hasLevelUps && !levelUpsDone && levelUpCeremonyReady
  const celebrationsReady = !hasLevelUps || levelUpsDone

  return (
    <>
    {/* Ceremony for each concept levelled up by this quiz. */}
    {showLevelUpCeremony && (
      <ConceptLevelUpCeremony
        transitions={upwardTransitions}
        gemsEarned={user ? correctCount : 0}
        totalGems={gemBalance}
        onResolved={() => setLevelUpsDone(true)}
      />
    )}
    {/* Streak flame (if today's streak grew) then quests cleared by this quiz —
        shown in that order, after the level-up ceremony and before the review
        content below. */}
    {celebrationsReady && (
      <PostQuizCelebrations streakEligible={STREAK_ENABLED && correctCount > 0} />
    )}
    {/* Today's Study Plan finished by this quiz — unlock the 2× gem bonus. */}
    {celebrationsReady && showPlanBonus && progressKey && (
      <StudyPlanCompleteOverlay
        progressKey={progressKey}
        gemsEarned={getDailyGems()}
        onClose={() => setShowPlanBonus(false)}
      />
    )}
    <ConceptPopup />

    {/* ── Pinned score header ───────────────────────────────────────
        Mirrors the Dashboard's pinned row: once the score card's actions have
        scrolled past, the score and icon-only copies of those actions stay at
        the top of the viewport. The wrapper is `h-0` on purpose so the bar
        floats over the review content instead of reserving space for itself —
        and so mounting it costs no layout shift at the top of the page. Sticky
        offsets match the nav chrome (the fixed top bar below lg, the sidebar at lg). */}
    <div ref={pinnedHeaderRef} className="sticky top-14 lg:top-0 z-20 h-0">
      <div
        aria-hidden={!actionsPinned}
        className={`border-b border-border/60 bg-background/95 backdrop-blur-md transition-all duration-200 ${
          actionsPinned ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="container max-w-2xl mx-auto px-4 py-2 flex items-center gap-3">
          <span className={`text-xl font-black tabular-nums leading-none ${scoreColorClass(percentage)}`}>
            {percentage}%
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatScore(points.earned)}/{formatScore(points.possible)}{points.unitPoints ? '' : ' pts'}
          </span>
          <div className="flex-1" />
          {user && (
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              aria-label="Go to Dashboard"
              title="Go to Dashboard"
              tabIndex={actionsPinned ? 0 : -1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground transition-colors hover:bg-secondary/80"
            >
              <LayoutDashboard className="h-5 w-5" />
            </button>
          )}
          {missedCount > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={handleReviewIncorrect}
                aria-label={`Review ${missedCount} incorrect`}
                title="Review Incorrect"
                tabIndex={actionsPinned ? 0 : -1}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-all hover:bg-primary/90 active:bg-primary/80 active:scale-[0.97]"
              >
                <XCircle className="h-5 w-5" />
              </button>
              <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-500 px-1 text-[11px] font-bold leading-none text-white shadow ring-2 ring-background tabular-nums">
                {missedCount}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>

    <div className="container max-w-2xl mx-auto px-4 py-8 space-y-6">

      {/* Back to where the quiz was started — an exam's builder, a study guide,
          Concept Craft. The review took the quiz's place in the history, so
          this is the browser's own Back too. */}
      <Link to={back.to} state={RESTORE_SCROLL} onClick={back.onLinkClick} aria-label={backName(back)} className="-mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {back.label}
      </Link>

      {/* ── First card: score header + concept coverage ──────────── */}
      <ConceptCoverageSection
        questions={session.questions}
        responses={session.responses}
        score={{
          mode: session.mode,
          percentage,
          correctCount,
          scoredPoints: points.earned,
          totalPoints: points.unitPoints ? undefined : points.possible,
          totalQuestions,
          timeTakenSeconds,
          conceptsLevelledUp: upwardTransitions.length,
          isLoggedIn: !!user,
          onSignIn: () => navigate('/auth', { state: { from: '/review' } }),
        }}
        selectedQuestion={selectedQuestion}
        onQuestionSelect={handleQuestionSelect}
        manualGrades={session.manualGrades}
        onReviewIncorrect={handleReviewIncorrect}
        levelUpTransitions={upwardTransitions}
        actionsRef={actionsRowRef}
      />

      {/* ── Question review ─────────────────────────────────────── */}
      {/* Scroll margin clears the pinned header (its own height plus the sticky
          offset at that breakpoint) so "Review Incorrect" doesn't park this
          heading underneath the bar it was pressed from. */}
      <div ref={questionReviewRef} className="space-y-2 scroll-mt-16 md:scroll-mt-28 lg:scroll-mt-16">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Question Review</h2>
          {selectedQuestion !== null && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
              Q{selectedQuestion + 1}
              <button
                type="button"
                onClick={() => setSelectedQuestion(null)}
                className="hover:opacity-70 transition-opacity"
                aria-label="Clear filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {selectedQuestion === null && showIncorrectOnly && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
              Incorrect only
              <button
                type="button"
                onClick={() => setShowIncorrectOnly(false)}
                className="hover:opacity-70 transition-opacity"
                aria-label="Clear filter"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
        <Separator />
      </div>

      <div className="space-y-4">
        {visibleQuestions.map((question) => {
          const idx = session.questions.indexOf(question)
          const chosen = session.responses[question.id]?.chosen ?? null
          const manualGrades = session.manualGrades ?? {}
          // Per-part grades keyed by label — covers both graded free-entry parts
          // (partManualGrades) and answerless essay parts (essaySelfGrades), which
          // the card reads from separate props but are stored under the same key.
          const partGrades = question.type === 'multi-part' ? Object.fromEntries(
            Object.entries(manualGrades)
              .filter(([k]) => k.startsWith(`${question.id}__`))
              .map(([k, v]) => [k.slice(question.id.length + 2), v])
          ) : undefined
          return (
            <div key={question.id} className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">
                Question {idx + 1}
                {unanswered[idx] && <span className="ml-1.5 text-muted-foreground">· Unanswered</span>}
              </p>
              <QuestionCard
                question={question}
                selectedAnswer={chosen}
                onAnswer={() => {/* read-only in review */}}
                showExplanation={true}
                showMeta={true}
                isLocked={true}
                selfGrade={question.type === 'free-entry' ? manualGrades[question.id] : undefined}
                partManualGrades={partGrades}
                essaySelfGrades={partGrades}
              />
            </div>
          )
        })}
      </div>

      {/* ── Actions ─────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3 pb-8">
        {retryIds.length > 0 && (
          <Button variant="outline" onClick={handleTryAgain} className="flex-1">
            Try Again ({[
              missedCount > 0 ? `${missedCount} missed` : null,
              unansweredCount > 0 ? `${unansweredCount} unanswered` : null,
            ].filter(Boolean).join(', ')})
          </Button>
        )}
        <Button onClick={handleNewQuiz} className="flex-1">
          New Quiz
        </Button>
      </div>
    </div>

      {/* ── Back to top ─────────────────────────────────────────── */}
      <button
        type="button"
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 right-6 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${showBackToTop ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      >
        <ArrowUp className="h-4 w-4" />
      </button>
    </>
  )
}
